import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type Ref,
} from 'react';
import type { PlanPoint, Size } from '@oscom/shared';
import { capture, usePanZoom } from '../plans/usePanZoom';
import { DRAG_THRESHOLD_PX, relativeToScreen, screenToRelative } from '../plans/viewport';

export interface PlanMarker {
  /** Unique on the plan, e.g. the placement id. */
  key: string;
  kind: 'point' | 'beacon';
  /** Visible text: the point number or the beacon name. */
  label: string;
  /** Relative position, 0..1 from the top left. */
  x: number;
  y: number;
  className: string;
  /** Full description; also the accessible name (colour is never the only signal). */
  title: string;
  /** In edit mode: whether the user may drag it (right of its object type). */
  movable?: boolean;
}

export interface PlanCanvasHandle {
  /** Fits the whole plan into the visible area ("Einpassen"). */
  fit: () => void;
}

export interface PlanCanvasProps {
  imageUrl: string;
  /** Natural pixel size of the image. */
  image: Size;
  /** Alternative text of the image: the plan name. */
  alt: string;
  /** Changing it remounts the `<img>`, i.e. loads the image again. */
  imageKey?: number;
  markers: PlanMarker[];
  /** `screen` is the marker position in container coordinates. */
  onMarkerClick: (key: string, screen: PlanPoint) => void;
  onImageError: () => void;
  /**
   * Called after every pan, zoom or fit, and when a marker drag starts.
   * Overlays anchored at a screen point (the popover) are stale then; the page
   * closes them.
   */
  onViewChange?: () => void;
  /** Edit mode: movable markers can be dragged. */
  editable?: boolean;
  /** An entry waits for its position: the next tap on the plan places it. */
  placing?: boolean;
  /** Tap on the plan while `placing`; relative position, clamped to the image. */
  onPlace?: (relative: PlanPoint) => void;
  /** A marker was dropped at a new relative position. */
  onMove?: (key: string, relative: PlanPoint) => void;
  /** Overlays positioned in container coordinates, e.g. the marker popover. */
  children?: ReactNode;
  ref?: Ref<PlanCanvasHandle>;
}

interface MarkerDrag {
  key: string;
  pointerId: number;
  start: PlanPoint;
  /** Largest distance from the start so far (container px). */
  moved: number;
  /** Current position, clamped to the image (container px). */
  screen: PlanPoint;
}

/**
 * Zoomable plan surface. The image sits on a transformed stage; the markers
 * do NOT: each is positioned at its transformed screen point, so it keeps its
 * size at every zoom level.
 *
 * In edit mode a press on a movable marker belongs to the marker (it does not
 * pan the plan): beyond DRAG_THRESHOLD_PX it is a move, otherwise a click.
 */
export function PlanCanvas({
  imageUrl,
  image,
  alt,
  imageKey = 0,
  markers,
  onMarkerClick,
  onImageError,
  onViewChange,
  editable = false,
  placing = false,
  onPlace,
  onMove,
  children,
  ref,
}: PlanCanvasProps): ReactNode {
  const containerRef = useRef<HTMLDivElement>(null);
  const { view, fit, toLocal, wasDrag, handlers } = usePanZoom(containerRef, image);
  // State for rendering, ref for the handlers: pointer events can arrive
  // faster than React renders.
  const [drag, setDragState] = useState<MarkerDrag | undefined>(undefined);
  const dragRef = useRef<MarkerDrag | undefined>(undefined);
  const setDrag = (next: MarkerDrag | undefined): void => {
    dragRef.current = next;
    setDragState(next);
  };
  // Travel of the last marker press; decides whether its click opens the popover.
  const lastMarkerPress = useRef(0);

  useImperativeHandle(ref, () => ({ fit }), [fit]);

  // Latest callback, so only a changed view triggers it.
  const viewChanged = useRef(onViewChange);
  useEffect(() => {
    viewChanged.current = onViewChange;
  }, [onViewChange]);
  useEffect(() => {
    viewChanged.current?.();
  }, [view]);

  // Leaving edit mode ends a drag in progress without saving.
  // So does a dragged marker that disappears (reload, hidden beacons); its
  // pointerup never arrives and the stale drag would block every further one.
  const draggedKey = drag?.key;
  const draggedPresent = draggedKey !== undefined && markers.some((m) => m.key === draggedKey);
  useEffect(() => {
    if (editable && (draggedKey === undefined || draggedPresent)) return;
    dragRef.current = undefined;
    setDragState(undefined);
  }, [editable, draggedKey, draggedPresent]);

  /** Client point -> container point, clamped to the image. */
  function clampedScreen(clientX: number, clientY: number): PlanPoint {
    return relativeToScreen(view, image, screenToRelative(view, image, toLocal(clientX, clientY)));
  }

  function onContainerClick(event: MouseEvent<HTMLDivElement>): void {
    if (!placing || !onPlace) return;
    // Clicks on markers and the popover are theirs; the end of a pan is no tap.
    if (event.target instanceof Element && event.target.closest('button, [data-plan-overlay]')) {
      return;
    }
    if (wasDrag()) return;
    onPlace(screenToRelative(view, image, toLocal(event.clientX, event.clientY)));
  }

  function markerPointerDown(marker: PlanMarker, event: PointerEvent<HTMLButtonElement>): void {
    lastMarkerPress.current = 0;
    if (!editable || !marker.movable || !onMove) return;
    // Primary button only; a second finger never starts a move.
    if (event.button !== 0 || dragRef.current) return;
    event.stopPropagation();
    capture(event.currentTarget, event.pointerId);
    const start = toLocal(event.clientX, event.clientY);
    setDrag({
      key: marker.key,
      pointerId: event.pointerId,
      start,
      moved: 0,
      screen: relativeToScreen(view, image, marker),
    });
  }

  function markerPointerMove(event: PointerEvent<HTMLButtonElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.stopPropagation();
    const local = toLocal(event.clientX, event.clientY);
    const moved = Math.max(drag.moved, Math.hypot(local.x - drag.start.x, local.y - drag.start.y));
    if (moved >= DRAG_THRESHOLD_PX && drag.moved < DRAG_THRESHOLD_PX) {
      // The popover anchored at the old position is stale now.
      viewChanged.current?.();
    }
    setDrag({
      ...drag,
      moved,
      screen:
        moved >= DRAG_THRESHOLD_PX ? clampedScreen(event.clientX, event.clientY) : drag.screen,
    });
  }

  function markerPointerUp(event: PointerEvent<HTMLButtonElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.stopPropagation();
    lastMarkerPress.current = drag.moved;
    setDrag(undefined);
    if (drag.moved >= DRAG_THRESHOLD_PX) {
      onMove?.(drag.key, screenToRelative(view, image, toLocal(event.clientX, event.clientY)));
    }
  }

  function markerPointerCancel(event: PointerEvent<HTMLButtonElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    // The marker goes back to its saved position.
    lastMarkerPress.current = drag.moved;
    setDrag(undefined);
  }

  const className = [
    'plan-canvas',
    editable ? 'plan-canvas--editing' : '',
    placing ? 'plan-canvas--placing' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={containerRef}
      className={className}
      tabIndex={0}
      role="region"
      aria-label="Planfläche: Ziehen verschiebt, Mausrad oder Plus und Minus zoomen, 0 passt ein"
      {...handlers}
      onClick={onContainerClick}
    >
      <div
        className="plan-stage"
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
      >
        <img
          key={imageKey}
          src={imageUrl}
          width={image.width}
          height={image.height}
          alt={alt}
          draggable={false}
          onError={onImageError}
        />
      </div>
      <div className="plan-markers">
        {markers.map((marker) => {
          const dragged = drag?.key === marker.key ? drag : undefined;
          const screen = dragged ? dragged.screen : relativeToScreen(view, image, marker);
          const movable = editable && marker.movable === true;
          return (
            <button
              key={marker.key}
              type="button"
              className={`${marker.className}${movable ? ' marker--movable' : ''}${
                dragged && dragged.moved >= DRAG_THRESHOLD_PX ? ' marker--dragging' : ''
              }`}
              style={{ left: `${screen.x}px`, top: `${screen.y}px` }}
              title={marker.title}
              aria-label={marker.title}
              onPointerDown={(event) => markerPointerDown(marker, event)}
              onPointerMove={markerPointerMove}
              onPointerUp={markerPointerUp}
              onPointerCancel={markerPointerCancel}
              onLostPointerCapture={markerPointerCancel}
              onClick={(event) => {
                event.stopPropagation();
                // A pointer click after a pan or a move is part of that gesture.
                // A keyboard activation (Enter/Space, `detail` 0) always counts.
                if (event.detail !== 0) {
                  const travelled = movable
                    ? lastMarkerPress.current >= DRAG_THRESHOLD_PX
                    : wasDrag();
                  if (travelled) return;
                }
                onMarkerClick(marker.key, screen);
              }}
            >
              {marker.kind === 'beacon' ? (
                <span className="marker__label">{marker.label}</span>
              ) : (
                marker.label
              )}
            </button>
          );
        })}
      </div>
      {children}
    </div>
  );
}
